import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import {
  ArrowBack,
  CloudUploadOutlined,
  Close,
  PictureAsPdf,
} from "@material-ui/icons";
import { connect } from "react-redux";
import HeaderDescription from "../../../components/commons/HeaderDescription";
import MOButtonV2 from "../../../components/commons/MOButtonV2";
import mobioApi from "../../../services";
import { toastMessage } from "../../../helpers/toastMessage";
import {
  KNOWLEDGE_SOURCE_TYPES,
  KNOWLEDGE_STATUSES,
  SOURCE_TYPE_OPTIONS,
  STATUS_OPTIONS,
} from "./knowledgeDocuments";
import {
  formatFileSize,
  getDocumentFile,
  isPdfFile,
  MAX_PDF_SIZE_BYTES,
} from "./knowledgeDocumentUtils";

const EMPTY_FORM = {
  title: "",
  source_type: KNOWLEDGE_SOURCE_TYPES.POLICY,
  status: KNOWLEDGE_STATUSES.DRAFT,
  file: null,
  fileName: "",
  fileSize: 0,
};

const KnowledgeDocumentForm = ({ mode, id, token }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const isEdit = mode === "edit";
  const [form, setForm] = useState(EMPTY_FORM);
  const [existingDocument, setExistingDocument] = useState(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (!isEdit) {
      return;
    }

    const getDocument = async () => {
      try {
        setLoading(true);
        const api = mobioApi(token);
        const response = await api.getDocument(id);
        const document = response?.data?.data;

        if (!document) {
          setExistingDocument(null);
          return;
        }

        const documentFile = getDocumentFile(document);
        setExistingDocument(document);
        setForm({
          title: document.title || "",
          source_type: document.source_type || KNOWLEDGE_SOURCE_TYPES.POLICY,
          status: document.status || KNOWLEDGE_STATUSES.DRAFT,
          file: null,
          fileName: documentFile.fileName || "",
          fileSize: documentFile.fileSize || 0,
        });
      } catch (error) {
        setExistingDocument(null);
        toastMessage(
          "error",
          error?.response?.data?.msg || "This document could not be loaded."
        );
      } finally {
        setLoading(false);
      }
    };

    getDocument();
  }, [id, isEdit, token]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const applyPdfFile = (file) => {
    if (!file) {
      return;
    }

    if (!isPdfFile(file)) {
      toastMessage("error", "Please upload a PDF file only.");
      return;
    }

    if (file.size > MAX_PDF_SIZE_BYTES) {
      toastMessage("error", "Please upload a PDF smaller than 10 MB.");
      return;
    }

    setForm((prev) => ({
      ...prev,
      file,
      fileName: file.name,
      fileSize: file.size,
      title: prev.title.trim() ? prev.title : file.name.replace(/\.pdf$/i, ""),
    }));
  };

  const handleFileInput = (event) => {
    applyPdfFile(event.target.files && event.target.files[0]);
    event.target.value = "";
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    applyPdfFile(event.dataTransfer.files && event.dataTransfer.files[0]);
  };

  const handleRemoveFile = () => {
    setForm((prev) => ({
      ...prev,
      file: null,
      fileName: "",
      fileSize: 0,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.title.trim()) {
      toastMessage("error", "Please add a title.");
      return;
    }

    if (!isEdit && !form.file) {
      toastMessage("error", "Please upload a PDF file.");
      return;
    }

    if (isEdit && !form.fileName && !form.file) {
      toastMessage("error", "Please upload a PDF file.");
      return;
    }

    const formData = new FormData();
    formData.append("title", form.title.trim());
    formData.append("source_type", form.source_type);
    formData.append("status", form.status);

    if (form.file) {
      formData.append("file", form.file);
    }

    setSaving(true);

    try {
      const api = mobioApi(token);

      if (isEdit) {
        const response = await api.updateDocument(id, formData);
        const updated = response?.data?.data;
        toastMessage("success", response?.data?.msg || "Document updated.");
        navigate(
          `/dashboard/ai-area/upload-documents/view/${updated?.id || id}`
        );
        return;
      }

      const response = await api.uploadDocument(formData);
      const created = response?.data?.data;
      toastMessage("success", response?.data?.msg || "Document added.");
      navigate(`/dashboard/ai-area/upload-documents/view/${created.id}`);
    } catch (error) {
      toastMessage(
        "error",
        error?.response?.data?.msg || "This document could not be saved."
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="ai-area-page">
        <button
          type="button"
          className="ai-area-back-btn"
          onClick={() => navigate("/dashboard/ai-area/upload-documents")}
        >
          <ArrowBack className="ai-area-back-icon" />
          Back to documents
        </button>
        <HeaderDescription
          heading="Update document"
          description="Loading document..."
        />
      </div>
    );
  }

  if (isEdit && !existingDocument) {
    return (
      <div className="ai-area-page">
        <button
          type="button"
          className="ai-area-back-btn"
          onClick={() => navigate("/dashboard/ai-area/upload-documents")}
        >
          <ArrowBack className="ai-area-back-icon" />
          Back to documents
        </button>
        <HeaderDescription
          heading="Document not found"
          description="This document is no longer available. Go back to the list to open another one."
        />
      </div>
    );
  }

  return (
    <div className="ai-area-page">
      <button
        type="button"
        className="ai-area-back-btn"
        onClick={() => navigate("/dashboard/ai-area/upload-documents")}
      >
        <ArrowBack className="ai-area-back-icon" />
        Back to documents
      </button>

      <HeaderDescription
        heading={isEdit ? "Update document" : "Add document"}
        description={
          isEdit
            ? "Change the title, type, status, or replace the PDF. The AI will use the latest version."
            : "Add a title and upload a PDF file. You can update it later if something changes."
        }
      />

      <form className="ai-area-document-form" onSubmit={handleSubmit}>
        <div className="ai-area-form-field">
          <label className="ai-area-form-label" htmlFor="title">
            Title
          </label>
          <input
            id="title"
            name="title"
            className="ai-area-form-input"
            value={form.title}
            onChange={handleChange}
            placeholder="For example, Airport pickup policy"
          />
        </div>

        <div className="ai-area-form-row">
          <div className="ai-area-form-field">
            <label className="ai-area-form-label" htmlFor="source_type">
              Type
            </label>
            <select
              id="source_type"
              name="source_type"
              className="ai-area-form-input"
              value={form.source_type}
              onChange={handleChange}
            >
              {SOURCE_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="ai-area-form-field">
            <label className="ai-area-form-label" htmlFor="status">
              Status
            </label>
            <select
              id="status"
              name="status"
              className="ai-area-form-input"
              value={form.status}
              onChange={handleChange}
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {isEdit && existingDocument && (
          <p className="ai-area-form-hint">
            Current version: {existingDocument.version}. Saving will create the
            next version.
          </p>
        )}

        <div className="ai-area-form-field">
          <label className="ai-area-form-label" htmlFor="pdf-upload">
            Document file
          </label>
          <input
            id="pdf-upload"
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="ai-area-file-input"
            onChange={handleFileInput}
          />

          {form.fileName ? (
            <div className="ai-area-pdf-file">
              <div className="ai-area-pdf-file-info">
                <PictureAsPdf className="ai-area-pdf-icon" />
                <div>
                  <p className="ai-area-pdf-file-name">{form.fileName}</p>
                  <p className="ai-area-pdf-file-meta">
                    PDF
                    {form.fileSize ? ` · ${formatFileSize(form.fileSize)}` : ""}
                  </p>
                </div>
              </div>
              <div className="ai-area-pdf-file-actions">
                <button
                  type="button"
                  className="ai-area-pdf-replace-btn"
                  onClick={() => fileInputRef.current && fileInputRef.current.click()}
                >
                  Replace
                </button>
                <button
                  type="button"
                  className="ai-area-pdf-remove-btn"
                  onClick={handleRemoveFile}
                  aria-label="Remove PDF"
                >
                  <Close fontSize="small" />
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className={`ai-area-pdf-dropzone ${
                isDragging ? "ai-area-pdf-dropzone-active" : ""
              }`}
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              onDragOver={(event) => {
                event.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
            >
              <CloudUploadOutlined className="ai-area-pdf-dropzone-icon" />
              <span className="ai-area-pdf-dropzone-title">
                Drop a PDF here or click to upload
              </span>
              <span className="ai-area-pdf-dropzone-hint">
                PDF files only, up to 10 MB
              </span>
            </button>
          )}
        </div>

        {isEdit && (
          <div className="ai-area-form-field">
            <label className="ai-area-form-label">Document text</label>
            {form.file ? (
              <p className="ai-area-form-hint">
                The text will update after you save the new PDF.
              </p>
            ) : existingDocument.content ? (
              <div className="ai-area-document-content-box">
                <p className="ai-area-document-content-text">
                  {existingDocument.content}
                </p>
              </div>
            ) : (
              <p className="ai-area-form-hint">
                No text is available for this document yet.
              </p>
            )}
          </div>
        )}

        <div className="ai-area-form-actions">
          <MOButtonV2
            type="button"
            text="Cancel"
            textColor="#4A5568"
            radius={4}
            backgroundColor="#EDF2F7"
            height={38}
            fontSize={13}
            padding="8px 18px"
            onClick={() => navigate("/dashboard/ai-area/upload-documents")}
          />
          <MOButtonV2
            type="submit"
            text={isEdit ? "Save changes" : "Save document"}
            textColor="#ffffff"
            radius={4}
            backgroundColor="#29AEE6"
            height={38}
            fontSize={13}
            padding="8px 18px"
            loading={saving}
          />
        </div>
      </form>
    </div>
  );
};

const mapState = (state) => ({
  token: state.userInfo.token,
});

export default connect(mapState)(KnowledgeDocumentForm);
